import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Injectable, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RequestService } from '@foundation/network/services';
import { NotificationService } from '@foundation/notification';
import { TranslateDirective, TranslationService } from '@foundation/translations/services';
import { finalize } from 'rxjs';

interface AdminMailAttempt {
	id: string;
	timeCreated?: string;
	timeUpdated?: string;
	status?: string;
	error?: string | null;
	mailId: string;
	mailSettingsId: string;
	mailSettingsName?: string | null;
	mailSettingsKind?: string | null;
}

interface AdminMail {
	id: string;
	timeCreated?: string;
	timeUpdated?: string;
	status?: string;
	subject: string;
	body: string;
	bodyHtml?: string | null;
	sender: string;
	priority: number;
	recipients: string[];
	attemptsCount: number;
	attempts: AdminMailAttempt[];
}

interface AdminMailSettings {
	id: string;
	name?: string | null;
	kind: string;
	isActive: boolean;
}

interface AdminMailOverview {
	mailSettings: AdminMailSettings[];
	mails: AdminMail[];
}

@Injectable({ providedIn: 'root' })
export class AdminEmailsRepository {
	private _requestService = inject(RequestService);

	getOverview$() {
		return this._requestService.getBasic$<AdminMailOverview>('/api/mails/admin/overview');
	}

	resendMail$(mailId: string) {
		return this._requestService.post$<{ message: string }, Record<string, never>>(`/api/mails/admin/${mailId}/resend`, {});
	}
}

@Component({
	selector: 'lib-admin-emails-page',
	standalone: true,
	imports: [CommonModule, FormsModule, TranslateDirective, DatePipe],
	templateUrl: './admin-emails-page.component.html',
	styleUrl: './admin-emails-page.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminEmailsPageComponent {
	private _repository = inject(AdminEmailsRepository);
	private _notificationService = inject(NotificationService);
	private _translationService = inject(TranslationService);

	private _i18n_loadError = this._translationService.prep('Failed to load emails.');
	private _i18n_resendMailConfirmation = this._translationService.prep('Are you sure you want to resend this email?');
	private _i18n_resendMailTitle = this._translationService.prep('Resend email');
	private _i18n_resend = this._translationService.prep('Resend');
	private _i18n_resendLaunched = this._translationService.prep('Email resend launched.');

	isLoading = signal(true);
	isRefreshing = signal(false);
	loadError = signal<string | null>(null);
	mailSettings = signal<AdminMailSettings[]>([]);
	mails = signal<AdminMail[]>([]);
	recipientFilter = signal('');
	activeResendMailId = signal<string | null>(null);
	expandedMailIds = signal<string[]>([]);

	filteredMails = computed(() => {
		const normalizedRecipientFilter = this.recipientFilter().trim().toLowerCase();
		if (!normalizedRecipientFilter) return this.mails();

		return this.mails().filter((mail) => mail.recipients.some((recipient) => recipient.toLowerCase().includes(normalizedRecipientFilter)));
	});

	constructor() {
		this.loadOverview();
	}

	loadOverview(isManualRefresh = false) {
		if (isManualRefresh) {
			if (this.isRefreshing()) return;
			this.isRefreshing.set(true);
		} else {
			this.isLoading.set(true);
		}

		this.loadError.set(null);
		this._repository
			.getOverview$()
			.pipe(
				finalize(() => {
					this.isLoading.set(false);
					this.isRefreshing.set(false);
				})
			)
			.subscribe({
				next: (response) => {
					if (response.error) {
						this.loadError.set(response.error.title || this._i18n_loadError());
						return;
					}

					this.mailSettings.set(response.result?.mailSettings ?? []);
					this.mails.set(response.result?.mails ?? []);
				},
				error: () => {
					this.loadError.set(this._i18n_loadError());
				},
			});
	}

	refresh() {
		this.loadOverview(true);
	}

	setRecipientFilter(recipientFilterValue: string) {
		this.recipientFilter.set(recipientFilterValue);
	}

	toggleExpandedMail(mailId: string) {
		if (this.isMailExpanded(mailId)) {
			this.expandedMailIds.set(this.expandedMailIds().filter((expandedMailId) => expandedMailId !== mailId));
			return;
		}

		this.expandedMailIds.set([...this.expandedMailIds(), mailId]);
	}

	isMailExpanded(mailId: string): boolean {
		return this.expandedMailIds().includes(mailId);
	}

	getMailStatusTone(status: string | undefined): string {
		switch (status) {
			case 'sent':
				return 'badge-success';
			case 'failed':
				return 'badge-error';
			case 'processing':
				return 'badge-warning';
			case 'pending':
				return 'badge-info';
			default:
				return 'badge-ghost';
		}
	}

	getAttemptStatusTone(status: string | undefined): string {
		if (status === 'sending') return 'badge-warning';
		return this.getMailStatusTone(status);
	}

	hasBodyHtml(mail: AdminMail): boolean {
		return !!mail.bodyHtml?.trim();
	}

	resendMail(mail: AdminMail) {
		if (this.activeResendMailId()) return;

		this._notificationService
			.confirm(this._i18n_resendMailConfirmation(), this._i18n_resendMailTitle(), {
				confirmButtonText: this._i18n_resend(),
			})
			.closed.subscribe((confirmed) => {
				if (!confirmed) return;

				this.activeResendMailId.set(mail.id);
				this._repository
					.resendMail$(mail.id)
					.pipe(
						finalize(() => {
							this.activeResendMailId.set(null);
						})
					)
					.subscribe({
						next: (response) => {
							if (response.error) {
								this._notificationService.error(response.error.title, response.error.description || '');
								return;
							}

							this._notificationService.snackSuccess(response.result?.message || this._i18n_resendLaunched());
							this.loadOverview(true);
						},
						error: () => {
							this._notificationService.error(this._i18n_loadError(), '');
						},
					});
			});
	}

	handleResendClick(mail: AdminMail, event: Event) {
		event.stopImmediatePropagation();
		this.resendMail(mail);
	}
}
